import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  approved: ["Approved", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"],
  pending_approval: ["Awaiting your approval", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
};

// Admin-only: document versions, corrections applied, and unresolved items needing approval.
export default function LegalReviewTab({ ctx }) {
  const [docs, setDocs] = useState(null);
  const load = () => axios.get(`${API}/admin/legal/review-flags`, { headers: { "X-Admin-Token": ctx.token } }).then((r) => setDocs(r.data.documents)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tighter text-white">Legal Review</h2>
          <p className="mt-2 text-sm font-medium text-white/50">Active document versions, the wording corrections applied, and the items that need your (or counsel's) decision. Visible to admins only.</p>
        </div>
        <button data-testid="legal-review-refresh" onClick={load} className="inline-flex items-center gap-2 text-sm font-bold text-white/60 hover:text-white"><RefreshCw size={15} /> Refresh</button>
      </div>
      <div className="mt-8 space-y-5">
        {(docs || []).map((d) => {
          const [label, cls] = STATUS[d.status] || [d.status, "bg-white/10 text-white/60 border-white/15"];
          return (
            <section key={d.slug} data-testid={`legal-review-${d.slug}`} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-extrabold text-white">{d.title}</h3>
                  <p className="text-xs font-semibold text-white/40">Version {d.version} · Effective Date {d.effective_date}{d.revision_of ? ` · revision of v${d.revision_of}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span data-testid={`legal-review-status-${d.slug}`} className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${cls}`}>{label}</span>
                  <a href={`/legal/${d.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#60a5fa]">View <ExternalLink size={12} /></a>
                </div>
              </div>
              {d.changes?.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-white/40">Corrections applied</p>
                  {d.changes.map((c, i) => <p key={i} className="mt-1.5 flex gap-2 text-sm text-white/70"><CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-400" />{c}</p>)}
                </div>
              )}
              {d.flags?.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-white/40">Items requiring approval</p>
                  {d.flags.map((f, i) => <p key={i} data-testid={`legal-review-flag-${d.slug}-${i}`} className="mt-1.5 flex gap-2 text-sm text-amber-100/80"><AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-400" />{f}</p>)}
                </div>
              )}
              {d.reviewed?.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-white/40">Reviewed — no change needed</p>
                  {d.reviewed.map((r, i) => <p key={i} className="mt-1.5 text-sm text-white/50">• {r}</p>)}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}
